import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Pencil,
  Trash2,
  Plus,
  Check,
  X,
  ImageIcon,
  GripVertical,
} from 'lucide-react';
import { adminProducts } from '../../services/api';
import { formatProductDisplay } from '../../utils/formatProduct';
import { Link } from 'react-router-dom';
import ImageUploadModal from '../../components/admin/ImageUploadModal';

const API_URL = process.env.REACT_APP_API_URL || '';
const imgSrc = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${API_URL}${url}`;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (v) => (v == null || v === '' ? '' : parseFloat(v).toFixed(2));

// ── Saved flash hook ──────────────────────────────────────────────────────────

function useSavedFlash() {
  const [flashes, setFlashes] = useState({});
  const timers = useRef({});

  const flash = useCallback((key) => {
    setFlashes((prev) => ({ ...prev, [key]: true }));
    if (timers.current[key]) clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => {
      setFlashes((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }, 2000);
  }, []);

  useEffect(() => {
    return () => {
      Object.values(timers.current).forEach(clearTimeout);
    };
  }, []);

  return [flashes, flash];
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ProductPricingList() {
  // Filter state
  const [search, setSearch] = useState('');
  const [wholesaleOnly, setWholesaleOnly] = useState(false);

  // Data state
  const [products, setProducts] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Expanded rows: Set of productIDs
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Tiers cache: { [productID]: tiersArray }
  const [tiersCache, setTiersCache] = useState({});

  // Tiers loading/error: { [productID]: 'loading' | 'error' | null }
  const [tiersStatus, setTiersStatus] = useState({});

  // Controlled inline fields: { [productID]: { wholesalePrice, wholesaleMinQty } }
  const [editingFields, setEditingFields] = useState({});

  // New tier inputs: { [productID]: { minQty: '', pricePerCase: '' } }
  const [newTierInputs, setNewTierInputs] = useState({});

  // Adding tier loading: { [productID]: bool }
  const [addingTier, setAddingTier] = useState({});

  // Editing a tier: { tierId, productID, minQty, pricePerCase } | null
  const [editingTier, setEditingTier] = useState(null);

  // Saving tier: { [tierId]: bool }
  const [savingTier, setSavingTier] = useState({});

  // Deleting tier: { [tierId]: bool }
  const [deletingTier, setDeletingTier] = useState({});

  // Saved flash indicators
  const [flashes, flash] = useSavedFlash();

  // Customer overrides modal: { productID, productName } | null
  const [overrideModal, setOverrideModal] = useState(null);
  const [overrideModalData, setOverrideModalData]     = useState([]);
  const [overrideModalLoading, setOverrideModalLoading] = useState(false);

  // Image upload modal: product object | null
  const [imageModal, setImageModal] = useState(null);

  // Featured toggle saving: { [productID]: bool }
  const [featToggles, setFeatToggles] = useState({});

  // Content edit panel
  const [editPanel, setEditPanel]           = useState(null);
  const [editPanelForm, setEditPanelForm]   = useState({});
  const [editPanelSaving, setEditPanelSaving] = useState(false);
  const [editPanelError, setEditPanelError] = useState('');
  const [editPanelSuccess, setEditPanelSuccess] = useState(false);

  // C7 sync controls
  const [editPanelC7LastSynced, setEditPanelC7LastSynced] = useState(null);
  const [c7Preview, setC7Preview]           = useState(null);
  const [c7PreviewLoading, setC7PreviewLoading] = useState(false);
  const [c7SyncLoading, setC7SyncLoading]   = useState(false);
  const [c7SyncError, setC7SyncError]       = useState('');
  const [bulkSyncLoading, setBulkSyncLoading] = useState(false);

  // Toast
  const [toastMsg, setToastMsg] = useState('');
  const toastTimer = useRef(null);

  // ── Load products ────────────────────────────────────────────────────────────

  const loadProducts = useCallback(() => {
    setLoading(true);
    setError('');
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (wholesaleOnly) params.wholesaleOnly = 'true';

    adminProducts
      .list(params)
      .then((res) => {
        if (res.data?.success) {
          const data = res.data.data || [];
          setProducts(data);

          // Initialize editingFields from fresh data (merge, not overwrite)
          setEditingFields((prev) => {
            const next = { ...prev };
            data.forEach((p) => {
              if (!next[p.productID]) {
                next[p.productID] = {
                  wholesalePrice: p.wholesalePrice != null ? fmt(p.wholesalePrice) : '',
                  wholesaleMinQty: p.wholesaleMinQty != null ? String(p.wholesaleMinQty) : '',
                };
              }
            });
            return next;
          });

          // Initialize newTierInputs for any new products
          setNewTierInputs((prev) => {
            const next = { ...prev };
            data.forEach((p) => {
              if (!next[p.productID]) {
                next[p.productID] = { minQty: '', pricePerCase: '' };
              }
            });
            return next;
          });
        } else {
          setError('Failed to load products.');
        }
      })
      .catch(() => {
        setError('An error occurred while loading products.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [search, wholesaleOnly]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // ── Toggle row expand ────────────────────────────────────────────────────────

  const toggleRow = useCallback(
    (productID) => {
      setExpandedRows((prev) => {
        const next = new Set(prev);
        if (next.has(productID)) {
          next.delete(productID);
        } else {
          next.add(productID);
          // Lazy-load tiers if not yet cached
          if (!tiersCache[productID]) {
            setTiersStatus((s) => ({ ...s, [productID]: 'loading' }));
            adminProducts
              .getTiers(productID)
              .then((res) => {
                if (res.data?.success) {
                  setTiersCache((c) => ({ ...c, [productID]: res.data.data || [] }));
                  setTiersStatus((s) => ({ ...s, [productID]: null }));
                } else {
                  setTiersStatus((s) => ({ ...s, [productID]: 'error' }));
                }
              })
              .catch(() => {
                setTiersStatus((s) => ({ ...s, [productID]: 'error' }));
              });
          }
        }
        return next;
      });
    },
    [tiersCache]
  );

  // ── isWholesale toggle ───────────────────────────────────────────────────────

  const handleWholesaleToggle = useCallback((productID, checked) => {
    // Optimistic local update
    setProducts((prev) =>
      prev.map((p) =>
        p.productID === productID ? { ...p, isWholesale: checked ? 1 : 0 } : p
      )
    );
    adminProducts
      .update(productID, { isWholesale: checked ? 1 : 0 })
      .then((res) => {
        if (res.data?.success) {
          flash(`wholesale-${productID}`);
        }
      })
      .catch(() => {
        // Revert on failure
        setProducts((prev) =>
          prev.map((p) =>
            p.productID === productID ? { ...p, isWholesale: checked ? 0 : 1 } : p
          )
        );
      });
  }, [flash]);

  // ── Inline field change ──────────────────────────────────────────────────────

  const handleFieldChange = useCallback((productID, field, value) => {
    setEditingFields((prev) => ({
      ...prev,
      [productID]: { ...prev[productID], [field]: value },
    }));
  }, []);

  // ── Inline field blur (save) ─────────────────────────────────────────────────

  const handleFieldBlur = useCallback(
    (productID, field) => {
      const value = editingFields[productID]?.[field];
      if (value == null) return;

      const payload = {};
      if (field === 'wholesalePrice') {
        payload.wholesalePrice = value === '' ? null : parseFloat(value);
      } else if (field === 'wholesaleMinQty') {
        payload.wholesaleMinQty = value === '' ? null : parseInt(value, 10);
      }

      adminProducts
        .update(productID, payload)
        .then((res) => {
          if (res.data?.success) {
            flash(`${field}-${productID}`);
            // Normalize display value
            if (field === 'wholesalePrice' && value !== '') {
              setEditingFields((prev) => ({
                ...prev,
                [productID]: {
                  ...prev[productID],
                  wholesalePrice: fmt(value),
                },
              }));
            }
          }
        })
        .catch(() => {
          // Silent failure; field keeps its value so user can retry
        });
    },
    [editingFields, flash]
  );

  // ── Add tier ─────────────────────────────────────────────────────────────────

  const handleAddTier = useCallback(
    async (productID) => {
      const inputs = newTierInputs[productID];
      if (!inputs || inputs.minQty === '' || inputs.pricePerCase === '') return;

      setAddingTier((prev) => ({ ...prev, [productID]: true }));
      try {
        const res = await adminProducts.createTier(productID, {
          minQty: parseInt(inputs.minQty, 10),
          pricePerCase: parseFloat(inputs.pricePerCase),
        });
        if (res.data?.success) {
          // Reload tiers for this product
          const tiersRes = await adminProducts.getTiers(productID);
          if (tiersRes.data?.success) {
            setTiersCache((c) => ({ ...c, [productID]: tiersRes.data.data || [] }));
            // Update tierCount on the product row
            setProducts((prev) =>
              prev.map((p) =>
                p.productID === productID
                  ? { ...p, tierCount: (tiersRes.data.data || []).length }
                  : p
              )
            );
          }
          // Reset inputs
          setNewTierInputs((prev) => ({
            ...prev,
            [productID]: { minQty: '', pricePerCase: '' },
          }));
        }
      } catch {
        // Silent; inputs remain so user can retry
      } finally {
        setAddingTier((prev) => ({ ...prev, [productID]: false }));
      }
    },
    [newTierInputs]
  );

  // ── Tier edit ─────────────────────────────────────────────────────────────────

  const startEditTier = useCallback((tier) => {
    setEditingTier({
      tierId: tier.tierID,
      productID: tier.productID,
      minQty: String(tier.minQty),
      pricePerCase: fmt(tier.pricePerCase),
    });
  }, []);

  const cancelEditTier = useCallback(() => {
    setEditingTier(null);
  }, []);

  const saveTier = useCallback(async () => {
    if (!editingTier) return;
    const { tierId, productID, minQty, pricePerCase } = editingTier;
    setSavingTier((prev) => ({ ...prev, [tierId]: true }));
    try {
      const res = await adminProducts.updateTier(productID, tierId, {
        minQty: parseInt(minQty, 10),
        pricePerCase: parseFloat(pricePerCase),
      });
      if (res.data?.success) {
        setTiersCache((c) => ({
          ...c,
          [productID]: (c[productID] || []).map((t) =>
            t.tierID === tierId
              ? { ...t, minQty: parseInt(minQty, 10), pricePerCase: parseFloat(pricePerCase) }
              : t
          ),
        }));
        setEditingTier(null);
      }
    } catch {
      // Silent; keep editing state
    } finally {
      setSavingTier((prev) => ({ ...prev, [tierId]: false }));
    }
  }, [editingTier]);

  // ── Tier delete ───────────────────────────────────────────────────────────────

  const deleteTier = useCallback(async (productID, tierId) => {
    if (!window.confirm('Delete this tier?')) return;
    setDeletingTier((prev) => ({ ...prev, [tierId]: true }));
    try {
      const res = await adminProducts.deleteTier(productID, tierId);
      if (res.data?.success) {
        setTiersCache((c) => ({
          ...c,
          [productID]: (c[productID] || []).filter((t) => t.tierID !== tierId),
        }));
        setProducts((prev) =>
          prev.map((p) =>
            p.productID === productID
              ? { ...p, tierCount: Math.max(0, (p.tierCount || 1) - 1) }
              : p
          )
        );
      }
    } catch {
      // Silent
    } finally {
      setDeletingTier((prev) => ({ ...prev, [tierId]: false }));
    }
  }, []);

  // ── Customer overrides modal ──────────────────────────────────────────────────

  const openOverrideModal = useCallback(async (product) => {
    setOverrideModal({ productID: product.productID, productName: product.proFanciful });
    setOverrideModalData([]);
    setOverrideModalLoading(true);
    try {
      const res = await adminProducts.getCustomerOverrides(product.productID);
      setOverrideModalData(res.data?.data || []);
    } catch {
      // silently fail — modal shows empty state
    } finally {
      setOverrideModalLoading(false);
    }
  }, []);

  // ── Toast ─────────────────────────────────────────────────────────────────────

  const showToast = useCallback((msg) => {
    setToastMsg(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(''), 4000);
  }, []);

  // ── Featured toggle ───────────────────────────────────────────────────────────

  const handleFeaturedToggle = useCallback(async (productID, checked) => {
    setFeatToggles((prev) => ({ ...prev, [productID]: true }));
    try {
      const res = await adminProducts.updateFeatured(productID, { isFeatured: checked });
      if (res.data?.success) {
        setProducts((prev) =>
          prev.map((p) =>
            p.productID === productID ? { ...p, isFeatured: checked ? 1 : 0 } : p
          )
        );
        showToast('Saved');
      }
    } catch {
      // silent
    } finally {
      setFeatToggles((prev) => ({ ...prev, [productID]: false }));
    }
  }, [showToast]);

  // ── Image saved callback ──────────────────────────────────────────────────────

  const handleImageSaved = useCallback((productID, url) => {
    setProducts((prev) =>
      prev.map((p) =>
        p.productID === productID ? { ...p, proLabelImageUrl: url || null } : p
      )
    );
  }, []);

  // ── Content edit panel ────────────────────────────────────────────────────────

  const openEditPanel = (product) => {
    setEditPanel(product);
    setEditPanelForm({
      proTastingNotes: product.proTastingNotes || '',
      proDescription:  product.proDescription  || '',
      proWinemaker:    product.proWinemaker     || '',
      proTechSheetUrl: product.proTechSheetUrl  || '',
      c7ProductId:     product.c7ProductId      || '',
    });
    setEditPanelC7LastSynced(product.c7LastSynced || null);
    setC7Preview(null);
    setC7SyncError('');
    setEditPanelError('');
    setEditPanelSuccess(false);
  };

  const saveEditPanel = useCallback(async () => {
    if (!editPanel) return;
    setEditPanelSaving(true);
    setEditPanelError('');
    setEditPanelSuccess(false);
    try {
      const res = await adminProducts.update(editPanel.productID, editPanelForm);
      if (res.data?.success) {
        setEditPanelSuccess(true);
        setTimeout(() => setEditPanelSuccess(false), 2000);
      } else {
        setEditPanelError('Failed to save. Please try again.');
      }
    } catch (err) {
      setEditPanelError(err?.response?.data?.message || 'Failed to save. Please try again.');
    } finally {
      setEditPanelSaving(false);
    }
  }, [editPanel, editPanelForm]);

  const handleC7Preview = useCallback(async () => {
    if (!editPanel) return;
    setC7PreviewLoading(true);
    setC7Preview(null);
    setC7SyncError('');
    try {
      const res = await adminProducts.previewC7(editPanel.productID);
      if (res.data?.success) {
        setC7Preview(res.data.data);
      } else {
        setC7SyncError(res.data?.error || 'Preview failed.');
      }
    } catch (err) {
      setC7SyncError(err?.response?.data?.message || 'Preview failed.');
    } finally {
      setC7PreviewLoading(false);
    }
  }, [editPanel]);

  const handleC7Sync = useCallback(async () => {
    if (!editPanel) return;
    setC7SyncLoading(true);
    setC7Preview(null);
    setC7SyncError('');
    try {
      const res = await adminProducts.syncC7(editPanel.productID);
      if (res.data?.success && res.data.data?.success) {
        const { fieldsUpdated } = res.data.data;
        showToast(fieldsUpdated.length
          ? `Synced from Commerce7: ${fieldsUpdated.join(', ')}`
          : 'Synced from Commerce7 — no changes'
        );
        // Re-fetch this product to refresh form fields
        const fresh = await adminProducts.get(editPanel.productID);
        if (fresh.data?.success) {
          const p = fresh.data.data;
          setEditPanelForm(prev => ({
            ...prev,
            proTastingNotes: p.proTastingNotes || '',
            proDescription:  p.proDescription  || '',
            proTechSheetUrl: p.proTechSheetUrl  || '',
          }));
          setEditPanelC7LastSynced(p.c7LastSynced || null);
          // Update the product row in the table
          setProducts(prev => prev.map(prod =>
            prod.productID === editPanel.productID ? { ...prod, ...p } : prod
          ));
        }
      } else {
        const errMsg = res.data?.data?.error || res.data?.message || 'Sync failed.';
        setC7SyncError(errMsg);
      }
    } catch (err) {
      setC7SyncError(err?.response?.data?.message || 'Sync failed.');
    } finally {
      setC7SyncLoading(false);
    }
  }, [editPanel, showToast]);

  const handleBulkC7Sync = useCallback(async () => {
    if (!window.confirm('This will overwrite tasting notes, descriptions, and images for all wholesale products that have a Commerce7 ID set. Continue?')) return;
    setBulkSyncLoading(true);
    try {
      const res = await adminProducts.syncC7Bulk();
      if (res.data?.success) {
        const { total, succeeded, skipped, failed } = res.data.data;
        showToast(`Synced ${succeeded} products, ${skipped} skipped, ${failed} failed (${total} total)`);
        loadProducts();
      } else {
        showToast('Bulk sync failed.');
      }
    } catch (err) {
      showToast('Bulk sync failed: ' + (err?.response?.data?.message || err.message));
    } finally {
      setBulkSyncLoading(false);
    }
  }, [showToast, loadProducts]);

  // ── Render tier sub-table ─────────────────────────────────────────────────────

  const renderTierSubTable = (product) => {
    const { productID } = product;
    const status = tiersStatus[productID];
    const tiers = tiersCache[productID];
    const inputs = newTierInputs[productID] || { minQty: '', pricePerCase: '' };
    const isAdding = addingTier[productID] || false;

    if (status === 'loading') {
      return (
        <div className="d-flex align-items-center gap-2 py-2 ps-3 text-muted small">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading tiers…</span>
          </div>
          Loading tiers…
        </div>
      );
    }

    if (status === 'error') {
      return (
        <div className="alert alert-danger alert-sm py-1 px-2 ms-4 my-2 mb-0 small">
          Failed to load tiers.
        </div>
      );
    }

    return (
      <div className="ms-4 my-2">
        <table className="table table-sm table-bordered w-auto mb-0">
          <thead className="table-light">
            <tr>
              <th>Min Qty</th>
              <th>Price / Case</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(tiers || []).length === 0 && (
              <tr>
                <td colSpan={3} className="text-muted text-center small fst-italic">
                  No tiers yet.
                </td>
              </tr>
            )}
            {(tiers || []).map((tier) => {
              const isEditingThis =
                editingTier && editingTier.tierId === tier.tierID;
              const isSaving = savingTier[tier.tierID] || false;
              const isDeleting = deletingTier[tier.tierID] || false;

              if (isEditingThis) {
                return (
                  <tr key={tier.tierID}>
                    <td>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        style={{ width: '80px' }}
                        value={editingTier.minQty}
                        min="1"
                        onChange={(e) =>
                          setEditingTier((prev) => ({
                            ...prev,
                            minQty: e.target.value,
                          }))
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        style={{ width: '100px' }}
                        value={editingTier.pricePerCase}
                        min="0"
                        step="0.01"
                        onChange={(e) =>
                          setEditingTier((prev) => ({
                            ...prev,
                            pricePerCase: e.target.value,
                          }))
                        }
                      />
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-success btn-sm d-flex align-items-center gap-1"
                          disabled={isSaving}
                          onClick={saveTier}
                        >
                          {isSaving ? (
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          ) : (
                            <Check size={13} />
                          )}
                          Save
                        </button>
                        <button
                          className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                          disabled={isSaving}
                          onClick={cancelEditTier}
                        >
                          <X size={13} />
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              }

              return (
                <tr key={tier.tierID}>
                  <td>{tier.minQty}</td>
                  <td>${fmt(tier.pricePerCase)}</td>
                  <td>
                    <div className="d-flex gap-1">
                      <button
                        className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                        disabled={isDeleting || !!editingTier}
                        onClick={() => startEditTier(tier)}
                        title="Edit tier"
                      >
                        <Pencil size={13} />
                        Edit
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1"
                        disabled={isDeleting || !!editingTier}
                        onClick={() => deleteTier(productID, tier.tierID)}
                        title="Delete tier"
                      >
                        {isDeleting ? (
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                          />
                        ) : (
                          <Trash2 size={13} />
                        )}
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}

            {/* Add tier row */}
            <tr className="table-light">
              <td>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  style={{ width: '80px' }}
                  placeholder="Min Qty"
                  min="1"
                  value={inputs.minQty}
                  disabled={isAdding}
                  onChange={(e) =>
                    setNewTierInputs((prev) => ({
                      ...prev,
                      [productID]: { ...prev[productID], minQty: e.target.value },
                    }))
                  }
                />
              </td>
              <td>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  style={{ width: '100px' }}
                  placeholder="Price/Case"
                  min="0"
                  step="0.01"
                  value={inputs.pricePerCase}
                  disabled={isAdding}
                  onChange={(e) =>
                    setNewTierInputs((prev) => ({
                      ...prev,
                      [productID]: {
                        ...prev[productID],
                        pricePerCase: e.target.value,
                      },
                    }))
                  }
                />
              </td>
              <td>
                <button
                  className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                  disabled={
                    isAdding || inputs.minQty === '' || inputs.pricePerCase === ''
                  }
                  onClick={() => handleAddTier(productID)}
                >
                  {isAdding ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                    />
                  ) : (
                    <Plus size={13} />
                  )}
                  Add
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // ── Render product row ─────────────────────────────────────────────────────

  const renderProductRows = (product) => {
    const {
      productID,
      proBrand,
      proFanciful,
      proVintage,
      proVarietal,
      isWholesale,
      isFeatured,
      inventory,
      tierCount,
      proLabelImageUrl,
    } = product;

    const isExpanded = expandedRows.has(productID);
    const fields = editingFields[productID] || { wholesalePrice: '', wholesaleMinQty: '' };

    const thumb = imgSrc(proLabelImageUrl);

    const rows = [
      <tr key={`product-${productID}`}>
        {/* Image thumbnail */}
        <td style={{ width: 56, padding: '4px 8px' }}>
          {thumb ? (
            <img
              src={thumb}
              alt=""
              style={{ width: 40, height: 56, objectFit: 'cover', borderRadius: 4, cursor: 'pointer', display: 'block' }}
              onClick={() => setImageModal(product)}
            />
          ) : (
            <div
              className="bg-light d-flex align-items-center justify-content-center"
              style={{ width: 40, height: 56, borderRadius: 4, cursor: 'pointer' }}
              onClick={() => setImageModal(product)}
            >
              <ImageIcon size={16} className="text-muted" />
            </div>
          )}
        </td>

        {/* Content edit button */}
        <td>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => openEditPanel(product)}
            title="Edit content"
          >
            <Pencil size={13} />
          </button>
        </td>

        {/* Expand toggle */}
        <td>
          <button
            className="btn btn-sm btn-link p-0 text-secondary d-flex align-items-center"
            onClick={() => toggleRow(productID)}
            title={isExpanded ? 'Collapse' : 'Expand tiers'}
          >
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        </td>

        {/* Product name */}
        <td>
          <div className="fw-semibold">{proBrand || '—'}</div>
          {(() => {
            const { secondary } = formatProductDisplay(product);
            return secondary ? <div className="text-muted small">{secondary}</div> : null;
          })()}
        </td>

        {/* Vintage */}
        <td>{proVintage || '—'}</td>

        {/* Varietal */}
        <td>{proVarietal || '—'}</td>

        {/* Wholesale? */}
        <td>
          <div className="d-flex align-items-center gap-2">
            <div className="form-check mb-0">
              <input
                type="checkbox"
                className="form-check-input"
                id={`wholesale-${productID}`}
                checked={!!isWholesale}
                onChange={(e) => handleWholesaleToggle(productID, e.target.checked)}
              />
            </div>
            {flashes[`wholesale-${productID}`] && (
              <span className="text-success small">&#10003; saved</span>
            )}
          </div>
        </td>

        {/* Featured toggle */}
        <td>
          <div className="d-flex align-items-center gap-2">
            <div className="form-check form-switch mb-0">
              <input
                type="checkbox"
                className="form-check-input"
                id={`featured-${productID}`}
                checked={!!isFeatured}
                disabled={!!featToggles[productID]}
                onChange={(e) => handleFeaturedToggle(productID, e.target.checked)}
              />
            </div>
            {featToggles[productID] ? (
              <span className="spinner-border spinner-border-sm text-secondary" role="status" style={{ width: 14, height: 14 }} />
            ) : (
              !!isFeatured && <GripVertical size={14} className="text-muted" title="Reorder on Featured Products page" />
            )}
          </div>
        </td>

        {/* Base Price */}
        <td>
          <div className="d-flex align-items-center gap-2">
            <div className="input-group input-group-sm" style={{ width: '110px' }}>
              <span className="input-group-text">$</span>
              <input
                type="number"
                className="form-control form-control-sm"
                value={fields.wholesalePrice}
                min="0"
                step="0.01"
                placeholder="0.00"
                onChange={(e) =>
                  handleFieldChange(productID, 'wholesalePrice', e.target.value)
                }
                onBlur={() => handleFieldBlur(productID, 'wholesalePrice')}
              />
            </div>
            {flashes[`wholesalePrice-${productID}`] && (
              <span className="text-success small">&#10003; saved</span>
            )}
          </div>
        </td>

        {/* Min Qty */}
        <td>
          <div className="d-flex align-items-center gap-2">
            <input
              type="number"
              className="form-control form-control-sm"
              style={{ width: '80px' }}
              value={fields.wholesaleMinQty}
              min="1"
              placeholder="—"
              onChange={(e) =>
                handleFieldChange(productID, 'wholesaleMinQty', e.target.value)
              }
              onBlur={() => handleFieldBlur(productID, 'wholesaleMinQty')}
            />
            {flashes[`wholesaleMinQty-${productID}`] && (
              <span className="text-success small">&#10003; saved</span>
            )}
          </div>
        </td>

        {/* Tiers */}
        <td>
          <button
            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
            onClick={() => toggleRow(productID)}
            title="View / edit tiers"
          >
            <span className="badge bg-secondary">{tierCount ?? 0}</span>
            {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          </button>
        </td>

        {/* Customer Overrides badge */}
        <td>
          {product.overrideCount > 0 ? (
            <button
              className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
              title="View customer overrides for this product"
              onClick={() => openOverrideModal(product)}
            >
              <span className="badge bg-primary">{product.overrideCount}</span>
              <span className="small">overrides</span>
            </button>
          ) : (
            <span className="text-muted small">—</span>
          )}
        </td>

        {/* Inventory */}
        <td>{inventory != null ? inventory : '—'}</td>
      </tr>,
    ];

    if (isExpanded) {
      rows.push(
        <tr key={`tiers-${productID}`} className="bg-light">
          <td colSpan={13} className="p-0">
            {renderTierSubTable(product)}
          </td>
        </tr>
      );
    }

    return rows;
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="container-fluid py-4">
      {/* Page header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0">Product Pricing</h4>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
            onClick={handleBulkC7Sync}
            disabled={bulkSyncLoading}
            title="Sync all wholesale products with a Commerce7 ID from Commerce7"
          >
            {bulkSyncLoading
              ? <span className="spinner-border spinner-border-sm" role="status" />
              : <RefreshCw size={14} />
            }
            Sync All from C7
          </button>
          <button
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
            onClick={loadProducts}
            title="Refresh"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error alert */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError('')}
            aria-label="Close"
          />
        </div>
      )}

      {/* Filter bar */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-center">
            {/* Search */}
            <div className="col-12 col-md-6">
              <label className="form-label small mb-1">Search</label>
              <div className="input-group input-group-sm">
                <span className="input-group-text">
                  <Search size={14} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Product name, varietal, vintage…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Wholesale-only toggle */}
            <div className="col-12 col-md-4 d-flex align-items-end">
              <div className="form-check form-switch mb-0 mt-auto pb-1">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="wholesaleOnlyToggle"
                  checked={wholesaleOnly}
                  onChange={(e) => setWholesaleOnly(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="wholesaleOnlyToggle">
                  Show wholesale-enabled only
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading spinner */}
      {loading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : products.length === 0 ? (
        /* Empty state */
        <div className="text-center text-muted py-5">
          No products found matching your filters.
        </div>
      ) : (
        /* Table */
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 56 }} />
                <th style={{ width: 40 }}>Content</th>
                <th style={{ width: '2%' }} />
                <th>Product</th>
                <th>Vintage</th>
                <th>Varietal</th>
                <th>Wholesale?</th>
                <th>Featured</th>
                <th>Base Price</th>
                <th>Min Qty</th>
                <th>Tiers</th>
                <th>Overrides</th>
                <th>Inventory</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => renderProductRows(product))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Content Edit Panel (offcanvas) ── */}
      {editPanel && (
        <>
          <div
            className="offcanvas-backdrop fade show"
            onClick={() => setEditPanel(null)}
          />
          <div
            className="offcanvas offcanvas-end show"
            tabIndex="-1"
            style={{ width: 'min(460px, 100vw)' }}
          >
            <div className="offcanvas-header border-bottom">
              <h5 className="offcanvas-title">{editPanel.proFanciful}</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setEditPanel(null)}
                disabled={editPanelSaving}
              />
            </div>

            <div className="offcanvas-body">
              {/* ── Commerce7 Sync ── */}
              <div className="mb-3 border rounded p-3 bg-light">
                <label className="form-label fw-semibold mb-1">Commerce7 Product ID</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Paste Commerce7 UUID to enable content sync"
                  value={editPanelForm.c7ProductId}
                  onChange={(e) =>
                    setEditPanelForm((prev) => ({ ...prev, c7ProductId: e.target.value }))
                  }
                />
                <div className="form-text">Paste the UUID from Commerce7 to enable content sync</div>
                {editPanelC7LastSynced && (
                  <div className="text-muted small mt-1">
                    Last synced: {new Date(editPanelC7LastSynced).toLocaleString('en-US')}
                  </div>
                )}

                {editPanelForm.c7ProductId.trim() && (
                  <div className="d-flex gap-2 mt-2">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleC7Preview}
                      disabled={c7PreviewLoading || c7SyncLoading}
                    >
                      {c7PreviewLoading
                        ? <span className="spinner-border spinner-border-sm me-1" role="status" />
                        : null
                      }
                      Preview from Commerce7
                    </button>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={handleC7Sync}
                      disabled={c7SyncLoading || c7PreviewLoading}
                    >
                      {c7SyncLoading
                        ? <span className="spinner-border spinner-border-sm me-1" role="status" />
                        : '↻ '
                      }
                      Sync from Commerce7
                    </button>
                  </div>
                )}

                {c7SyncError && (
                  <div className="alert alert-danger py-1 px-2 mt-2 mb-0 small">{c7SyncError}</div>
                )}

                {c7Preview && !c7Preview.skipped && !c7Preview.error && (
                  <div className="alert alert-info py-2 px-3 mt-2 mb-0 small">
                    {(() => {
                      const fields = ['proTastingNotes', 'proDescription', 'proLabelImageUrl', 'proTechSheetUrl'];
                      const changes = fields.filter(f =>
                        c7Preview.proposed?.[f] && c7Preview.proposed[f] !== c7Preview.current[f]
                      );
                      if (!changes.length) return <span>No changes — already up to date.</span>;
                      return (
                        <>
                          <div className="fw-semibold mb-1">Would update: {changes.join(', ')}</div>
                          {changes.map(f => (
                            <div key={f} className="mb-1">
                              <span className="text-muted">{f}:</span>{' '}
                              {String(c7Preview.proposed[f] || '').slice(0, 100)}
                              {String(c7Preview.proposed[f] || '').length > 100 ? '…' : ''}
                            </div>
                          ))}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Tasting Notes</label>
                <textarea
                  rows={5}
                  className="form-control"
                  placeholder="Tasting notes visible to customers in the product drawer..."
                  value={editPanelForm.proTastingNotes}
                  onChange={(e) =>
                    setEditPanelForm((prev) => ({ ...prev, proTastingNotes: e.target.value }))
                  }
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Wine Story / About This Wine</label>
                <textarea
                  rows={4}
                  className="form-control"
                  placeholder="Background, winemaking approach, or story..."
                  value={editPanelForm.proDescription}
                  onChange={(e) =>
                    setEditPanelForm((prev) => ({ ...prev, proDescription: e.target.value }))
                  }
                />
                <div className="form-text">Appears in the product drawer under 'About This Wine'</div>
              </div>

              <div className="mb-3">
                <label className="form-label">Winemaker</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Jeremy Trettevik"
                  value={editPanelForm.proWinemaker}
                  onChange={(e) =>
                    setEditPanelForm((prev) => ({ ...prev, proWinemaker: e.target.value }))
                  }
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Tech Sheet URL</label>
                <input
                  type="url"
                  className="form-control"
                  placeholder="https://..."
                  value={editPanelForm.proTechSheetUrl}
                  onChange={(e) =>
                    setEditPanelForm((prev) => ({ ...prev, proTechSheetUrl: e.target.value }))
                  }
                />
                <div className="form-text">
                  Direct link to a PDF. A download button appears in the product drawer when this is set.
                </div>
              </div>
            </div>

            <div className="p-3 border-top d-flex align-items-center justify-content-between gap-2">
              <div>
                {editPanelError && (
                  <div className="alert alert-danger py-1 px-2 mb-0 small">{editPanelError}</div>
                )}
                {editPanelSuccess && !editPanelError && (
                  <span className="text-success small">Saved ✓</span>
                )}
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setEditPanel(null)}
                  disabled={editPanelSaving}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                  onClick={saveEditPanel}
                  disabled={editPanelSaving}
                >
                  {editPanelSaving && (
                    <span className="spinner-border spinner-border-sm" role="status" />
                  )}
                  Save
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Image Upload Modal ── */}
      {imageModal && (
        <ImageUploadModal
          product={imageModal}
          onClose={() => setImageModal(null)}
          onSaved={(url) => handleImageSaved(imageModal.productID, url)}
        />
      )}

      {/* ── Toast ── */}
      {toastMsg && (
        <div className="toast-container position-fixed bottom-0 end-0 p-3" style={{ zIndex: 9999 }}>
          <div className="toast show align-items-center text-bg-success border-0">
            <div className="d-flex">
              <div className="toast-body">{toastMsg}</div>
            </div>
          </div>
        </div>
      )}

      {/* ── Customer Overrides Modal ── */}
      {overrideModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,.45)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Customer Overrides — {overrideModal.productName}
                </h5>
                <button className="btn-close" onClick={() => setOverrideModal(null)} />
              </div>
              <div className="modal-body p-0">
                {overrideModalLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" />
                  </div>
                ) : overrideModalData.length === 0 ? (
                  <div className="text-center text-muted py-4">No overrides found.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Customer</th>
                          <th>Override Price</th>
                          <th>Price Level</th>
                          <th>Set By</th>
                          <th>Date</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {overrideModalData.map((row) => (
                          <tr key={row.overrideID}>
                            <td className="fw-semibold">{row.companyName}</td>
                            <td>${fmt(row.customPrice)}</td>
                            <td>
                              <span className="badge bg-secondary text-capitalize">
                                {row.priceLevel || 'standard'}
                              </span>
                            </td>
                            <td className="text-muted small">{row.setByName || '—'}</td>
                            <td className="text-muted small">
                              {new Date(row.created_at).toLocaleDateString('en-US')}
                            </td>
                            <td>
                              <Link
                                to={`/admin/customers/${row.customerID}#pricing`}
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => setOverrideModal(null)}
                              >
                                View
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setOverrideModal(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
